import ApiPbxClient from "@/api/ApiPbxClient";
import { TRANSFER_QUEUE, WAITING_QUEUE } from "@/utils/constants";
import qs from "qs";

export const ANSWERING = 1;
export const ATTENDING = 2;
export const COMPLETING = 3;

const state = {
  callsStatus: {},
  lastCall: {},
  isOnCall: false,
  isOnConference: false,
  onCallStage: ANSWERING,
  lastOnCallChange: 0,
  currentAsteriskOpToCall: null,
};

const mutations = {
  setCallsStatus(state, status) {
    state.callsStatus = status;
  },

  setCurrentAsteriskOpToCall(state, asteriskOpToCall) {
    state.currentAsteriskOpToCall = asteriskOpToCall;
  },

  setLastCall(state, data) {
    state.lastCall = data;
  },

  setIsExtensionOnCall(state, { extension, active }) {
    const ext = `${extension}`.substr(-3);
    const currentExtension = state.callsStatus.endpoints.find((endpoint) => endpoint.ext === ext);
    currentExtension.onCall = active;
  },

  isOnCall(state, value) {
    state.lastOnCallChange = new Date().getTime();
    state.isOnCall = value;
  },

  isOnConference(state, value) {
    state.isOnConference = value;
  },

  // We don't want to change the state from the API if not more than 3 seconds have pass from "active" isOnCall change.
  setNotOnCallFromApi(state) {
    const now = new Date().getTime();
    if (state.lastOnCallChange < now - 3000) {
      state.lastOnCallChange = now;
      state.isOnCall = false;
      state.isOnConference = false;
    }
  },

  setOnCallStage(state, value) {
    state.onCallStage = value;
  },
};

const actions = {
  updateCallsStatus({ commit, rootGetters, getters, state }, data) {
    commit("setCallsStatus", data);

    const ext = rootGetters["clientUserRegisterData/deskNumber"];
    if (ext) {
      const isOnCall = getters.endpoints.find((endpoint) => endpoint.ext == ext)?.onCall || false;
      if (!isOnCall && getters.isOnCall) {
        commit("setNotOnCallFromApi");
      }
    }
  },

  async answerCall({ commit, rootGetters, dispatch }, asteriskId) {
    const phoneExt = await dispatch("clientUserRegisterData/getPhoneExt", null, { root: true });
    if (!phoneExt) return false;

    const params = {
      wgId: JSON.stringify(rootGetters["app/selectedWorkgroupsIdsIncludingSelectedCentrals"]),
      asteriskId,
      phoneExt,
    };

    try {
      const api = ApiPbxClient();
      const { data } = await api.put("/calls/answer", null, { params });
      if (!data) throw new Error("No data returned from answerCall");
      commit("setIsExtensionOnCall", { extension: phoneExt, active: true });
      commit("setLastCall", data);
      dispatch("setIsOnCall", true);
      return data;
    } catch (error) {
      dispatch("setIsOnCall", false);
      console.error("answerCall error: ", error.message);
    }
  },

  async answerLastCall({ commit, rootGetters, dispatch }) {
    const phoneExt = await dispatch("clientUserRegisterData/getPhoneExt", null, { root: true });
    if (!phoneExt) return false;

    const params = {
      wgId: JSON.stringify(rootGetters["app/selectedWorkgroupsIdsIncludingSelectedCentrals"]),
      asteriskId: "older",
      phoneExt,
    };

    const api = ApiPbxClient();
    const { data } = await api.put("/calls/answer", null, { params });
    commit("setIsExtensionOnCall", { extension: phoneExt, active: true });
    commit("setLastCall", data);
    dispatch("setIsOnCall", true);
    return data;
  },

  async hangUp({ dispatch, commit, state }, payload) {
    const phoneExt = await dispatch("clientUserRegisterData/getPhoneExt", null, { root: true });
    if (!phoneExt) return false;

    const data = {
      servIds: payload?.tripId ? [payload?.tripId] : undefined,
    };

    const params = {
      asteriskId: payload?.asteriskId ? payload?.asteriskId : state?.currentAsteriskOpToCall,
      phoneExt,
    };

    const api = ApiPbxClient();
    try {
      await api.put("/calls/hangup", data, { params });
      dispatch("setIsOnCall", false);
      commit("setCurrentAsteriskOpToCall", null);
      return true;
    } catch (error) {
      console.error("hangUp error: ", error.message);
      // We want to free the phone even if the response is an error
      dispatch("setIsOnCall", false);
      return false;
    }
  },

  async setIsOnConference({ commit }, isOnConference) {
    commit("isOnConference", isOnConference);
  },

  async setIsOnCall({ commit }, isOnCall) {
    commit("isOnCall", isOnCall);
    if (isOnCall) {
      commit("setOnCallStage", ANSWERING);
      setTimeout(() => {
        commit("setOnCallStage", ATTENDING);
      }, 5000);
    } else {
      commit("isOnConference", false);
    }
  },

  setOnCallStage({ commit }, stage) {
    commit("setOnCallStage", stage);
  },

  async holdCall({ dispatch, state }, asteriskId) {
    const phoneExt = await dispatch("clientUserRegisterData/getPhoneExt", null, { root: true });
    if (!phoneExt) return false;

    try {
      const params = {
        asteriskId: asteriskId ? asteriskId : state.currentAsteriskOpToCall,
        phoneExt,
      };
      const api = ApiPbxClient();
      await api.put("/calls/hold", null, { params });
      dispatch("setIsOnCall", false);
    } catch (error) {
      console.error("holdCall error: ", error.message);
    }
  },

  async queueTransfer(_, asteriskId) {
    const params = {
      asteriskId,
    };
    const api = ApiPbxClient();
    await api.put("/calls/queueTransfer", null, { params });
  },

  async dropCall({ dispatch, commit }) {
    const phoneExt = await dispatch("clientUserRegisterData/getPhoneExt", null, { root: true });
    if (!phoneExt) return false;

    const params = {
      phoneExt,
    };
    const api = ApiPbxClient();
    try {
      await api.put("/calls/dropCall", null, { params, timeout: 1000 });
    } catch (e) {
      console.error("dropCall error: ", e.message);
    }

    dispatch("setIsOnConference", false);
    dispatch("setIsOnCall", false);
    commit("setCurrentAsteriskOpToCall", null);
  },

  async joinCalls({ dispatch }, asteriskIdCallB) {
    const phoneExt = await dispatch("clientUserRegisterData/getPhoneExt", null, { root: true });
    if (!phoneExt) return false;

    const params = {
      asteriskIdCallB,
      phoneExt,
    };
    const api = ApiPbxClient();
    await api.put("/calls/joinCalls", null, { params });
    dispatch("setIsOnConference", true);
  },

  async callOpToPhone({ dispatch, commit }, customData) {
    const phoneExt = await dispatch("clientUserRegisterData/getPhoneExt", null, { root: true });
    const urlFormData = qs.stringify(customData);

    try {
      dispatch("setIsOnCall", true);
      const api = ApiPbxClient();
      const response = await api.post("/calls/callOpToPhone", urlFormData, {
        headers: { "content-type": "application/x-www-form-urlencoded" },
      });
      commit("setLastCall", {
        ...response.data,
        calledNumber: response.data.callerNumber,
        callerNumber: response.data.calledNumber,
      });
      commit("setIsExtensionOnCall", { extension: phoneExt, active: true });
      commit("setCurrentAsteriskOpToCall", response.data.asteriskId);
      dispatch("setIsOnCall", true);
    } catch (error) {
      dispatch("setIsOnCall", false);
      console.error("ERROR en callOpToPhone: ", error.message);
      return error;
    }
  },
};

const getters = {
  calls(state) {
    if (state.callsStatus) {
      if (state.callsStatus.calls?.length) {
        let calls = state.callsStatus.calls;
        if (calls.length) {
          return calls;
        }
      } else {
        return [];
      }
    } else {
      return [];
    }
  },

  realCalls(state, getters) {
    return (
      getters.calls?.filter(
        (c) => c.queueType !== TRANSFER_QUEUE && c.queueType !== WAITING_QUEUE
      ) || []
    );
  },

  waitingForMergeCalls(state, getters) {
    return getters.calls?.filter((c) => c.queueType === TRANSFER_QUEUE) || [];
  },

  waitingCalls(state, getters) {
    return getters.calls?.filter((c) => c.queueType === WAITING_QUEUE) || [];
  },

  endpoints(state) {
    if (state.callsStatus.endpoints) {
      return state.callsStatus.endpoints.filter(
        (endpoint) => endpoint.type === "phone" && endpoint.state === "online"
      );
    }
  },

  hasCalls(state, getters) {
    if (getters.calls) {
      return getters.calls.length > 0;
    }
  },

  lastCall(state) {
    if (state.lastCall) {
      return state.lastCall;
    }
  },

  isOnCall(state) {
    return state.isOnCall;
  },

  isOnConference(state) {
    return state.isOnConference;
  },

  onCallStage(state) {
    return state.onCallStage;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
